import React, { useState, useEffect, useCallback, useRef } from 'react';
import TopSearchBar from './JobSearch/TopSearchBar';
import JobListingList from './JobSearch/JobListingList';
import JobDetailPanel from './JobSearch/JobDetailPanel';
import MobileFilterModal from './JobSearch/MobileFilterModal';
import { saveSearchConditions, loadSearchConditions, SearchFilters as StoredFilters } from '../utils/searchStorage';
import { trackJobSearch } from '../utils/tracking';
import * as styles from './JobSearchPage.module.scss';

interface JobSearchPageProps {
  initialKeyword?: string;
  initialFilters?: SearchFilters;
  initialData?: {
    job_listings: any[];
    pagination: any;
    total_job_listings_count?: number;
  };
  totalJobListingsCount?: number;
  currentUserId?: number;
  isUserSignedIn?: boolean;
  isProfileComplete?: boolean;
  isServerRendered?: boolean; // SSRかどうかのフラグ
}

interface SearchFilters {
  keyword?: string;
  skill_ids?: number[];
  occupation_ids?: number[];
  industry_ids?: number[];
  characteristic_ids?: number[];
  prefecture_ids?: number[];
  workstyle_types?: string[];
  availability_types?: string[];
  price_over?: number;
  payment_site?: string;
  settlement_hours?: string;
  interview_count_max?: number;
  published_within?: number;
  is_recruiting?: boolean;
  remote_types?: string[];
  workday_types?: string[];
  job_source_ids?: number[];
  sort?: string;
  page?: number;
}

const JobSearchPage: React.FC<JobSearchPageProps> = ({
  initialKeyword,
  initialFilters,
  initialData,
  totalJobListingsCount,
  currentUserId,
  isUserSignedIn = false,
  isProfileComplete = false,
  isServerRendered = false
}) => {
  const [filters, setFilters] = useState<SearchFilters>(() => {
    
    const defaultFilters = { 
      keyword: initialKeyword || '', 
      page: 1, 
      sort: 'created_at desc', 
      is_recruiting: false, 
      prefecture_ids: [],
      skill_ids: [],
      occupation_ids: [],
      industry_ids: [],
      characteristic_ids: [],
      workstyle_types: [],
      availability_types: [],
      remote_types: [],
      workday_types: [],
      job_source_ids: []
    };
    
    if (initialFilters) {
      // Clean initialFilters to remove null values that would override defaults
      const cleanedFilters = Object.fromEntries(
        Object.entries(initialFilters).filter(([_, v]) => v != null)
      );
      
      const result = {
        ...defaultFilters,
        ...cleanedFilters,
        // 配列フィールドは確実に配列として設定
        skill_ids: initialFilters.skill_ids || [],
        occupation_ids: initialFilters.occupation_ids || [],
        industry_ids: initialFilters.industry_ids || [],
        characteristic_ids: initialFilters.characteristic_ids || [],
        prefecture_ids: initialFilters.prefecture_ids || [],
        workstyle_types: initialFilters.workstyle_types || [],
        availability_types: initialFilters.availability_types || [],
        remote_types: initialFilters.remote_types || [],
        workday_types: initialFilters.workday_types || [],
        job_source_ids: initialFilters.job_source_ids || []
      };
      return result;
    }
    
    return defaultFilters;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [jobListings, setJobListings] = useState(initialData?.job_listings || []);
  const [pagination, setPagination] = useState(initialData?.pagination || null);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJobDetail, setSelectedJobDetail] = useState<any>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isHydrated, setIsHydrated] = useState(isServerRendered);
  const hasInitialData = useRef(!!initialData?.job_listings?.length);
  
  // スキル、職種、業界、特徴、都道府県の名前を格納するstate
  const [skillNames, setSkillNames] = useState<{[key: number]: string}>({});
  const [occupationNames, setOccupationNames] = useState<{[key: number]: string}>({});
  const [industryNames, setIndustryNames] = useState<{[key: number]: string}>({});
  const [characteristicNames, setCharacteristicNames] = useState<{[key: number]: string}>({});
  const [prefectureNames, setPrefectureNames] = useState<{[key: number]: string}>({});
  const [jobSourceNames, setJobSourceNames] = useState<{[key: number]: string}>({});

  // クライアント側でのマウント検知
  useEffect(() => {
    setIsHydrated(true);
    
    // SSRでない場合のみURLから初期状態を復元
    if (!isServerRendered) {
      const params = new URLSearchParams(window.location.search);
      const filtersFromUrl = parseUrlParams(params);
      setFilters(filtersFromUrl);
    }
  }, [isServerRendered]);

  // スキル、職種、業界、特徴、都道府県の名前を取得するuseEffect
  useEffect(() => {
    const fetchNames = async () => {
      try {
        // スキルの名前を取得
        if (filters.skill_ids && filters.skill_ids.length > 0) {
          const skillsResponse = await fetch('/api/v1/skills');
          const skillsData = await skillsResponse.json();
          const skillNameMap: {[key: number]: string} = {};
          
          // スキルAPIは階層構造なので平坦化する
          const allSkills: any[] = [];
          skillsData.forEach((category: any) => {
            if (category.skills) {
              category.skills.forEach((skill: any) => {
                allSkills.push(skill);
              });
            }
          });
          
          allSkills.forEach((skill: any) => {
            if (filters.skill_ids?.includes(skill.id)) {
              skillNameMap[skill.id] = skill.name;
            }
          });
          setSkillNames(skillNameMap);
        }

        // 職種の名前を取得
        if (filters.occupation_ids && filters.occupation_ids.length > 0) {
          const occupationsResponse = await fetch('/api/v1/occupations');
          const occupationsData = await occupationsResponse.json();
          const occupationNameMap: {[key: number]: string} = {};
          
          // 職種APIも階層構造なので平坦化する
          const allOccupations: any[] = [];
          occupationsData.forEach((occupation: any) => {
            allOccupations.push(occupation);
            if (occupation.children && occupation.children.length > 0) {
              occupation.children.forEach((child: any) => {
                allOccupations.push(child);
              });
            }
          });
          
          allOccupations.forEach((occupation: any) => {
            if (filters.occupation_ids?.includes(occupation.id)) {
              occupationNameMap[occupation.id] = occupation.name;
            }
          });
          setOccupationNames(occupationNameMap);
        }

        // 業界の名前を取得
        if (filters.industry_ids && filters.industry_ids.length > 0) {
          const industriesResponse = await fetch('/api/v1/industries');
          const industriesData = await industriesResponse.json();
          const industryNameMap: {[key: number]: string} = {};
          industriesData.forEach((industry: any) => {
            if (filters.industry_ids?.includes(industry.id)) {
              industryNameMap[industry.id] = industry.name;
            }
          });
          setIndustryNames(industryNameMap);
        }

        // 特徴の名前を取得
        if (filters.characteristic_ids && filters.characteristic_ids.length > 0) {
          const characteristicsResponse = await fetch('/api/v1/characteristics');
          const characteristicsData = await characteristicsResponse.json();
          const characteristicNameMap: {[key: number]: string} = {};
          characteristicsData.forEach((characteristic: any) => {
            if (filters.characteristic_ids?.includes(characteristic.id)) {
              characteristicNameMap[characteristic.id] = characteristic.name;
            }
          });
          setCharacteristicNames(characteristicNameMap);
        }

        // 都道府県の名前を取得
        if (filters.prefecture_ids && filters.prefecture_ids.length > 0) {
          const prefecturesResponse = await fetch('/api/v1/prefectures');
          const prefecturesData = await prefecturesResponse.json();
          const prefectureNameMap: {[key: number]: string} = {};
          prefecturesData.forEach((prefecture: any) => {
            if (filters.prefecture_ids?.includes(prefecture.id)) {
              prefectureNameMap[prefecture.id] = prefecture.name;
            }
          });
          setPrefectureNames(prefectureNameMap);
        }

        // フリーランスエージェントの名前を取得
        if (filters.job_source_ids && filters.job_source_ids.length > 0) {
          const jobSourcesResponse = await fetch('/api/v1/job_sources/for_search_filters');
          const jobSourcesData = await jobSourcesResponse.json();
          const jobSourceNameMap: {[key: number]: string} = {};
          jobSourcesData.forEach((jobSource: any) => {
            if (filters.job_source_ids?.includes(jobSource.id)) {
              jobSourceNameMap[jobSource.id] = jobSource.display_name || jobSource.name;
            }
          });
          setJobSourceNames(jobSourceNameMap);
        }
      } catch (error) {
        console.error('Failed to fetch names:', error);
      }
    };

    fetchNames();
  }, [filters.skill_ids, filters.occupation_ids, filters.industry_ids, filters.characteristic_ids, filters.prefecture_ids, filters.job_source_ids]);

  const fetchJobListings = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryString = buildQueryString(filters); // Already includes page parameter
      const response = await fetch(`/api/v1/job_listings/search${queryString}`, {
        headers: { 'Accept': 'application/json' }
      });
      const data = await response.json();
      setJobListings(data.job_listings);
      setPagination(data.pagination);

      // 検索イベントを送信（検索結果が取得できた後）
      if (data.pagination && data.pagination.total_count !== undefined) {
        trackJobSearch(filters, data.pagination.total_count);
      }
    } catch (error) {
      console.error('Failed to fetch job listings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // ブラウザの戻る/進むボタンに対応
  useEffect(() => {
    if (!isHydrated) return;

    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const filtersFromUrl = parseUrlParams(params);
      setFilters(filtersFromUrl);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isHydrated]);

  // フィルタ変更時にAPIリクエスト＆URL更新（SSR初回はスキップ）
  useEffect(() => {
    if (!isHydrated) return; // SSR時はスキップ
    
    // 初回ハイドレーション時は既にデータがあるのでAPIコールをスキップ
    if (hasInitialData.current && isServerRendered) {
      hasInitialData.current = false; // フラグをリセット
      return;
    }
    
    // Local Storageに検索条件を保存
    saveSearchConditions({
      keyword: filters.keyword,
      skill_ids: filters.skill_ids,
      occupation_ids: filters.occupation_ids,
      industry_ids: filters.industry_ids,
      characteristic_ids: filters.characteristic_ids,
      prefecture_ids: filters.prefecture_ids,
      workstyle_types: filters.workstyle_types,
      availability_types: filters.availability_types,
      price_over: filters.price_over,
      is_recruiting: filters.is_recruiting,
      sort: filters.sort
    });
    
    fetchJobListings();
    
    // URLを更新（常にpushStateを使用して履歴を保持）
    updateUrl(false);
  }, [filters, isHydrated, fetchJobListings, isServerRendered]);

  const updateUrl = (replaceState = false) => {
    if (typeof window === 'undefined') return; // SSR時のガード
    
    const queryString = buildQueryString(filters);
    const newUrl = `${window.location.pathname}${queryString}`;
    
    // 現在のURLと異なる場合のみ履歴を更新
    if (window.location.href !== newUrl) {
      if (replaceState) {
        window.history.replaceState({}, '', newUrl);
      } else {
        window.history.pushState({}, '', newUrl);
      }
    }
  };

  const updateFilters = (newFilters: Partial<SearchFilters>) => {
    // pageが明示的に指定されている場合はそれを使用、そうでない場合は1にリセット
    const pageValue = newFilters.page !== undefined ? newFilters.page : 1;
    const isPageChange = newFilters.page !== undefined;
    
    const newState = { ...filters, ...newFilters, page: pageValue };
    
    setFilters(newState);
    
    // 即座にURLを更新
    const queryString = buildQueryString(newState);
    const newUrl = `${window.location.pathname}${queryString}`;
    
    if (window.location.href !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
    
    // ページネーションクリック時のスクロール動作
    if (isPageChange) {
      setTimeout(() => {
        if (window.innerWidth <= 768) {
          // モバイル: ページトップにスクロール
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          // デスクトップ: 求人一覧コンテナの先頭にスクロール
          const targetElement = document.querySelector('[data-job-listings]');
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 300);
    }
  };

  const removeFilter = (filterKey: string, value?: any) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      // valueが指定されていない場合は、フィルター全体を削除
      if (value === undefined) {
        delete newFilters[filterKey as keyof SearchFilters];
      } else if (filterKey.endsWith('_ids')) {
        // ID配列のフィルター（skill_ids, occupation_ids, industry_ids, characteristic_ids, job_source_ids, prefecture_ids）
        const arrayKey = filterKey as keyof Pick<SearchFilters, 'skill_ids' | 'occupation_ids' | 'industry_ids' | 'characteristic_ids' | 'job_source_ids' | 'prefecture_ids'>;
        if (newFilters[arrayKey]) {
          // 型の不一致を防ぐため、両方を数値に変換して比較
          const valueNum = typeof value === 'string' ? parseInt(value, 10) : value;
          newFilters[arrayKey] = newFilters[arrayKey]!.filter(id => {
            const idNum = typeof id === 'string' ? parseInt(id, 10) : id;
            return idNum !== valueNum;
          });
          if (newFilters[arrayKey]!.length === 0) {
            delete newFilters[arrayKey];
          }
        }
      } else if (filterKey === 'workstyle_types' || filterKey === 'availability_types' || filterKey === 'remote_types' || filterKey === 'workday_types') {
        // 文字列配列のフィルター（workstyle_types, availability_types, remote_types, workday_types）
        const arrayKey = filterKey as keyof Pick<SearchFilters, 'workstyle_types' | 'availability_types' | 'remote_types' | 'workday_types'>;
        if (newFilters[arrayKey]) {
          // 文字列として比較（型変換を考慮）
          newFilters[arrayKey] = newFilters[arrayKey]!.filter(type => String(type) !== String(value));
          if (newFilters[arrayKey]!.length === 0) {
            delete newFilters[arrayKey];
          }
        }
      } else {
        delete newFilters[filterKey as keyof SearchFilters];
      }
      
      return { ...newFilters, page: 1 };
    });
  };

  const handleJobSelect = async (jobId: number) => {
    setSelectedJobId(jobId);
    setIsLoading(true);
    
    try {
      const response = await fetch(`/api/v1/job_listings/${jobId}`, {
        headers: { 'Accept': 'application/json' }
      });
      const jobDetail = await response.json();
      setSelectedJobDetail(jobDetail);
    } catch (error) {
      console.error('Failed to fetch job detail:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobClose = () => {
    setSelectedJobId(null);
    setSelectedJobDetail(null);
  };

  return (
    <div>
      <TopSearchBar 
        keyword={filters.keyword || ''}
        prefectureIds={filters.prefecture_ids || []}
        priceOver={filters.price_over}
        isRecruiting={filters.is_recruiting || false}
        remoteTypes={filters.remote_types || []}
        skillIds={filters.skill_ids || []}
        occupationIds={filters.occupation_ids || []}
        industryIds={filters.industry_ids || []}
        workdayTypes={filters.workday_types || []}
        characteristicIds={filters.characteristic_ids || []}
        jobSourceIds={filters.job_source_ids || []}
        onSearch={(searchFilters: SearchFilters) => updateFilters(searchFilters)}
      />
      
      <div className="container-fluid">
        <div className="row g-4 mt-0">
          {/* 求人一覧（左カラム） */}
          <div className="col-12 col-md-4 col-lg-4 d-flex flex-column" data-job-listings="true">
            <JobListingList
            jobListings={jobListings}
            selectedJobId={selectedJobId || undefined}
            onJobSelect={handleJobSelect}
            totalCount={pagination?.total_count || 0}
            from={pagination?.from || 0}
            to={pagination?.to || 0}
            currentPage={filters.page || 1}
            perPage={pagination?.per_page || 40}
            onPageChange={(page) => updateFilters({ page })}
            sortBy={filters.sort || 'created_at desc'}
            onSortChange={(sort) => updateFilters({ sort })}
            searchFilters={filters}
            onRemoveFilter={removeFilter}
            skillNames={skillNames}
            occupationNames={occupationNames}
            industryNames={industryNames}
            characteristicNames={characteristicNames}
            prefectureNames={prefectureNames}
            isLoading={isLoading}
          />
          </div>
          
          {/* 求人詳細（右カラム） */}
          <div className="col-md-8 col-lg-8 d-none d-md-block">
            <JobDetailPanel
              jobDetail={selectedJobDetail}
              onClose={handleJobClose}
              currentUserId={currentUserId}
              isUserSignedIn={isUserSignedIn}
              isProfileComplete={isProfileComplete}
              isLoading={isLoading}
              totalJobListingsCount={totalJobListingsCount}
            />
          </div>
        </div>
      </div>

      {/* SP用フィルタモーダル（クライアント側のみ） */}
      {isHydrated && (
        <MobileFilterModal 
          show={showMobileFilters}
          filters={filters}
          onFilterChange={updateFilters}
          onClose={() => setShowMobileFilters(false)}
        />
      )}
    </div>
  );
};

// ヘルパー関数
const parseUrlParams = (params: URLSearchParams): SearchFilters => {
  return {
    keyword: params.get('keyword') || '',
    skill_ids: params.getAll('skill_ids[]').map(Number).filter(Boolean),
    occupation_ids: params.getAll('occupation_ids[]').map(Number).filter(Boolean),
    industry_ids: params.getAll('industry_ids[]').map(Number).filter(Boolean),
    characteristic_ids: params.getAll('characteristic_ids[]').map(Number).filter(Boolean),
    prefecture_ids: params.getAll('prefecture_ids[]').map(Number).filter(Boolean),
    workstyle_types: params.getAll('workstyle_types[]'),
    availability_types: params.getAll('availability_types[]'),
    price_over: params.get('price_over') ? Number(params.get('price_over')) : undefined,
    payment_site: params.get('payment_site') || '',
    settlement_hours: params.get('settlement_hours') || '',
    interview_count_max: params.get('interview_count_max') ? Number(params.get('interview_count_max')) : undefined,
    published_within: params.get('published_within') ? Number(params.get('published_within')) : undefined,
    is_recruiting: params.get('is_recruiting') === 'true',
    remote_types: params.getAll('remote_types[]'),
    workday_types: params.getAll('workday_types[]'),
    job_source_ids: params.getAll('job_source_ids[]').map(Number).filter(Boolean),
    page: parseInt(params.get('page') || '1'),
    sort: params.get('sort') || 'created_at desc'
  };
};

const buildQueryString = (filters: SearchFilters): string => {
  const params = new URLSearchParams();
  
  if (filters.keyword) params.append('keyword', filters.keyword);
  filters.skill_ids?.forEach(id => params.append('skill_ids[]', id.toString()));
  filters.occupation_ids?.forEach(id => params.append('occupation_ids[]', id.toString()));
  filters.industry_ids?.forEach(id => params.append('industry_ids[]', id.toString()));
  filters.characteristic_ids?.forEach(id => params.append('characteristic_ids[]', id.toString()));
  filters.prefecture_ids?.forEach(id => params.append('prefecture_ids[]', id.toString()));
  filters.workstyle_types?.forEach(type => params.append('workstyle_types[]', type));
  filters.availability_types?.forEach(type => params.append('availability_types[]', type));
  if (filters.price_over) params.append('price_over', filters.price_over.toString());
  if (filters.payment_site) params.append('payment_site', filters.payment_site);
  if (filters.settlement_hours) params.append('settlement_hours', filters.settlement_hours);
  if (filters.interview_count_max) params.append('interview_count_max', filters.interview_count_max.toString());
  if (filters.published_within) params.append('published_within', filters.published_within.toString());
  if (filters.is_recruiting) params.append('is_recruiting', 'true');
  filters.remote_types?.forEach(type => params.append('remote_types[]', type));
  filters.workday_types?.forEach(type => params.append('workday_types[]', type));
  filters.job_source_ids?.forEach(id => params.append('job_source_ids[]', id.toString()));
  if (filters.sort) params.append('sort', filters.sort);
  if (filters.page && filters.page > 1) params.append('page', filters.page.toString());
  
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

export default JobSearchPage;
